'use client';

import React, { useEffect } from 'react';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { ButtonProps as MuiButtonProps, SxProps, Theme } from '@mui/material';
import type { AddToBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddToBasketFragmentFragmentDoc';
import type { ButtonProps } from '@packages/shared/src/components/Button/Button';
import { Button } from '@packages/shared/src/components/Button/Button';
import { CircularProgress } from '@packages/shared/src/components/CircularProgress/CircularProgress';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';
import { Link } from '@packages/shared/src/components/Link/Link';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Cart32 } from '@packages/themes/icons';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { type MessageDescriptor, useIntl } from 'react-intl';
import type {
  AddToBasketFragmentFragment,
  LineItemFragmentFragment,
} from '@packages/gql/generated/shopping/graphql';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogFooter } from '@packages/shared/src/ui/Dialog/DialogFooter/DialogFooter';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader.client';
import { addToBasketMessages } from './messages';
import { useBasketData } from './useBasketData';

export type AddToBasketButtonProps = {
  /**
   * addToBasket fragment data
   */
  data: FragmentType<typeof AddToBasketFragmentFragmentDoc>;
  /**
   * AddToBasketLayer component
   */
  children?: (
    layerData: AddToBasketFragmentFragment,
    lineItem: LineItemFragmentFragment,
  ) => React.ReactNode;
  /**
   * Glycerin tracking props
   */
  clickTrackingProps?: ButtonProps['clickTrackingProps'];
  /**
   * Please use only for miniDV
   * If this value is undefined, it will add new product in to the basket
   * If this value is defined, it will update the current lineItem in the basket
   */
  lineItemId?: string;
  /**
   * Please use only for miniDV
   * onClose function to close the miniDV
   * If this value is undefined, it will add new product into the basket from detail view.
   * If this value is defined, and lineItemId is undefined, it will add new product into the basket from miniDV.
   * If this value is defined, and lineItemId is defined, it will update the current lineItem in the basket from miniDV.
   */
  onClose?: () => void;
  /**
   * Custom button text
   */
  customButtonText?: MessageDescriptor | null;
  /**
   * Creates unique icon theming
   */
  iconSx?: SxProps<Theme>;
  /**
   * @default false
   */
  hideEndIcon?: boolean;
} & Omit<MuiButtonProps, 'children'>;

/**
 *
 * @deprecated please use `AddToBasketButtonTailwind` instead from `@packages/modules/src/AddToBasketButton/AddToBasketButtonTailwind.client.tsx`
 */
export const AddToBasketButton = ({
  data: productData,
  children,
  clickTrackingProps,
  customButtonText,
  lineItemId,
  onClose,
  onClick,
  sx: sxButtonOverrides,
  color = 'primary',
  size = 'large',
  fullWidth = true,
  hideEndIcon = false,
  iconSx,
}: AddToBasketButtonProps) => {
  const config = useConfig();
  const dispatchGTMEvent = useTracking();
  const { locale } = useI18n();
  const { formatMessage } = useIntl();
  const { loggedIn } = useLoginState();
  const {
    product,
    isAvailable,
    isLoading,
    dialogOpened,
    miniDVOnClickHandler,
    onClickHandler,
    trackToCheckAndOrderHandler,
    layerCloseHandler,
    handleButtonText,
    lineItemForDialog,
  } = useBasketData({ productData, lineItemId, onClose, customButtonText });

  useEffect(() => {
    if (dialogOpened) {
      dispatchDisplayOverlayTracking({
        eventDispatcher: dispatchGTMEvent,
        displayTrackingProps: {
          category: 'A2B-Layer',
          label: 'A2B',
        },
      });
    }
  }, [dialogOpened, dispatchGTMEvent]);

  const endIcon = (() => {
    if (hideEndIcon) {
      return null;
    }
    if (isLoading) {
      return <CircularProgress color="secondary" size="s" sx={iconSx} />;
    }
    if (!isLoading && lineItemId === undefined) {
      return <Cart32 sx={{ color: 'primary.contrastText', ...iconSx }} />;
    }

    return null;
  })();

  return (
    <>
      {/* If there is no onClose function, it means button was clicked from detail view page */}
      {dialogOpened && lineItemForDialog && onClose === undefined && (
        <Dialog isOpen={dialogOpened} onClose={layerCloseHandler}>
          <DialogHeader onClose={layerCloseHandler}>
            {formatMessage(addToBasketMessages.headline)}
          </DialogHeader>
          <DialogContent>{children && children(product, lineItemForDialog)}</DialogContent>
          <DialogFooter>
            <>
              {loggedIn && (
                <Button
                  LinkComponent={Link}
                  clickTrackingProps={{
                    category: 'A2B-Layer',
                    label: 'A2B',
                    detail: 'Zur Kasse',
                  }}
                  onClick={trackToCheckAndOrderHandler}
                  href={getLocalizedPath(config.order.paths.payment, locale)}
                  color="primary"
                  size="large"
                  sx={{ maxWidth: ['100%', '100%', '180px'] }}
                  fullWidth
                >
                  {formatMessage(addToBasketMessages.toCheckout)}
                </Button>
              )}
              <Button
                LinkComponent={Link}
                clickTrackingProps={{
                  category: 'A2B-Layer',
                  label: 'A2B',
                  detail: 'Zum Warenkorb',
                }}
                onClick={trackToCheckAndOrderHandler}
                href={getLocalizedPath(config.order.paths.basket, locale)}
                color={loggedIn ? 'inherit' : 'primary'}
                size="large"
                sx={{ maxWidth: ['100%', '100%', '180px'] }}
                fullWidth
              >
                {formatMessage(addToBasketMessages.navigateToCart)}
              </Button>
              <Button
                clickTrackingProps={{
                  category: 'A2B-Layer',
                  label: 'A2B',
                  detail: 'Weiter shoppen',
                }}
                variant="text"
                size="large"
                onClick={layerCloseHandler}
                sx={{ maxWidth: ['100%', '100%', '180px'] }}
                fullWidth
              >
                {formatMessage(addToBasketMessages.continueShopping)}
              </Button>
            </>
          </DialogFooter>
        </Dialog>
      )}
      <Button
        endIcon={endIcon}
        color={color}
        size={size}
        onClick={(e) => {
          onClick?.(e);
          lineItemId ? miniDVOnClickHandler(e) : onClickHandler(e);
        }}
        fullWidth={fullWidth}
        sx={{ whiteSpace: 'nowrap', width: '100%', ...sxButtonOverrides }}
        disabled={!isAvailable || isLoading}
        clickTrackingProps={clickTrackingProps}
      >
        {customButtonText !== null && formatMessage(handleButtonText())}
      </Button>
    </>
  );
};
